import { useCallback, useMemo, useRef, useState } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { EVIDENCE_FIELDS, evidenceKey } from '../../constants/evidenceFields';
import type { ObservationMedia } from '../../types/observationMedia';
import {
  confirmUpload, getObservationMedia, removeObservationMedia, requestUploadUrl, uploadToBlob,
} from '../../services/observationMediaService';
import { FieldEvidenceControl } from './FieldEvidenceButton';

interface FieldEvidenceCellButtonProps {
  /** The row's own test — batch grids have one WorkOrderDetail per row. */
  workOrderDetailKey: string;
  /** Formula-Registry-style id ('pointLoad', …) — must exist in EVIDENCE_FIELDS. */
  testTypeId: string;
  /** Short field key from the test type's EVIDENCE_FIELDS entry. */
  fieldKey: string;
  /**
   * Whether this row already has evidence, from the page's one batched
   * field-summary call — drives the button state until the row's media is
   * lazily loaded on first open.
   */
  presumedHas: boolean;
  readOnly?: boolean;
  /** Fires after an upload or removal so the page can refresh its nag count. */
  onEvidenceChange?: (workOrderDetailKey: string, hasEvidence: boolean) => void;
}

/**
 * Standalone per-row "Add Evidence" button for batch grids (PLT): shows
 * status from the page's bulk field-summary and fetches the row's actual
 * media only when opened — one grid of 30 rows costs one summary call, not 30.
 */
export const FieldEvidenceCellButton = ({
  workOrderDetailKey, testTypeId, fieldKey, presumedHas, readOnly = false, onEvidenceChange,
}: FieldEvidenceCellButtonProps) => {
  const { canEditTestData } = usePermissions();
  const field = useMemo(
    () => (EVIDENCE_FIELDS[testTypeId] ?? []).find((f) => f.fieldKey === fieldKey),
    [testTypeId, fieldKey],
  );
  const fullKey = evidenceKey(testTypeId, fieldKey);

  const [items, setItems] = useState<ObservationMedia[]>([]);
  const [loaded, setLoaded] = useState(false);
  const fetchedRef = useRef(false);

  const notify = useCallback((next: ObservationMedia[]) => {
    onEvidenceChange?.(workOrderDetailKey, next.length > 0);
  }, [onEvidenceChange, workOrderDetailKey]);

  const handleOpen = useCallback(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getObservationMedia(workOrderDetailKey)
      .then((data) => setItems(data.filter((m) => m.UploadStatus === 'complete' && m.FieldKey === fullKey)))
      .catch(() => { /* control shows empty; retry on next mount */ })
      .finally(() => setLoaded(true));
  }, [workOrderDetailKey, fullKey]);

  const upload = useCallback(async (file: File, onProgress?: (pct: number) => void) => {
    const { observationMediaKey, uploadUrl } = await requestUploadUrl(workOrderDetailKey, {
      FileName: file.name,
      FileSize: file.size,
      MimeType: file.type || 'application/octet-stream',
      FieldKey: fullKey,
    });
    await uploadToBlob(uploadUrl, file, file.type, onProgress);
    const confirmed = await confirmUpload(observationMediaKey);
    setItems((prev) => {
      const next = [confirmed, ...prev];
      notify(next);
      return next;
    });
  }, [workOrderDetailKey, fullKey, notify]);

  const remove = useCallback(async (item: ObservationMedia) => {
    await removeObservationMedia(item.ObservationMediaKey);
    setItems((prev) => {
      const next = prev.filter((m) => m.ObservationMediaKey !== item.ObservationMediaKey);
      notify(next);
      return next;
    });
  }, [notify]);

  if (!field) return null;

  return (
    <FieldEvidenceControl
      label={field.label}
      required={field.required}
      items={items}
      loaded={loaded}
      presumedHas={presumedHas}
      canEdit={canEditTestData && !readOnly}
      onOpen={handleOpen}
      upload={upload}
      remove={remove}
      dense
    />
  );
};
